import { useState, useCallback } from 'react';
import { type VehicleSelectionValue } from '@/shared/components/VehicleEnrichment';
import { type SearchClientResponse } from '@/features/guides/api/generated';

export enum SearchStep {
  ProblemInput = 'problem',
  SearchResults = 'results'
}

export interface ProblemSearchState {
  currentStep: SearchStep;
  problemDescription: string;
  vehicleData: VehicleSelectionValue;
  searchResults: SearchClientResponse | null;
}

export const useProblemSearch = () => {
  const [state, setState] = useState<ProblemSearchState>({
    currentStep: SearchStep.ProblemInput,
    problemDescription: '',
    vehicleData: {},
    searchResults: null
  });

  const handleProblemSubmit = useCallback((problem: string, vehicle: VehicleSelectionValue) => {
    setState(prev => ({
      ...prev,
      problemDescription: problem,
      vehicleData: vehicle,
      currentStep: SearchStep.SearchResults
    }));
  }, []);

  const handleBackToProblem = useCallback(() => {
    setState(prev => ({
      ...prev,
      searchResults: null,
      currentStep: SearchStep.ProblemInput
    }));
  }, []);

  const handleSearchComplete = useCallback((results: SearchClientResponse) => {
    setState(prev => ({
      ...prev,
      searchResults: results
    }));
  }, []);

  const handleStartOver = useCallback(() => {
    setState({
      currentStep: SearchStep.ProblemInput,
      problemDescription: '',
      vehicleData: {},
      searchResults: null
    });
  }, []);

  const generateSearchKey = useCallback(() => {
    const vehicleKey = `${state.vehicleData.manufacturer || ''}-${state.vehicleData.modelSeries || ''}-${state.vehicleData.engineBaseType || ''}`;
    return `${state.problemDescription}-${vehicleKey}`;
  }, [state.problemDescription, state.vehicleData]);

  return {
    ...state,
    actions: {
      handleProblemSubmit,
      handleBackToProblem,
      handleSearchComplete,
      handleStartOver
    },
    generateSearchKey
  };
};